import React from "react";
import { useTranslation } from "react-i18next";
import { IoGlobeOutline } from "react-icons/io5";

import { useLanguage } from "../../hooks/useLanguage";
import { useUserSettings } from "../../context/UserSettingsContext";
import "./LanguageSwitcher.css";

/**
 * Переключатель языка интерфейса.
 * Значения берутся из реестра языков (i18n/config.js).
 *
 * variant:
 *   "select" — обычный выпадающий список (по умолчанию);
 *   "pills"  — компактные кнопки с коротким кодом языка (RU / EN / AR),
 *              активный язык выделяется цветом.
 * withGlobe — показать перед кнопками иконку планеты (как в макете).
 */
function LanguageSwitcher({
    className = "",
    compact = false,
    variant = "select",
    withGlobe = false,
}) {
    const { t } = useTranslation("common");
    const { language, languages, setLanguage } = useLanguage();
    const { update } = useUserSettings();

    const handleChange = (value) => {
        setLanguage(value);
        // Запоминаем выбор в настройках пользователя (users.settings),
        // чтобы при следующем входе язык подставлялся по умолчанию.
        update({ language: value });
    };

    if (variant === "pills") {
        return (
            <div
                className={`language-switcher language-switcher--pills ${className}`.trim()}
                role="group"
                aria-label={t("language.label")}
            >
                {withGlobe && !compact && (
                    <IoGlobeOutline
                        className="language-switcher__globe"
                        size={16}
                        aria-hidden="true"
                    />
                )}
                {languages.map((item) => {
                    const isActive = item.code === language;
                    return (
                        <button
                            key={item.code}
                            type="button"
                            className={`language-pill ${isActive ? "active" : ""}`}
                            onClick={() => handleChange(item.code)}
                            aria-pressed={isActive}
                            title={item.label}
                        >
                            {item.short || item.code.toUpperCase()}
                        </button>
                    );
                })}
            </div>
        );
    }

    return (
        <div className={`language-switcher ${className}`.trim()}>
            {!compact && (
                <label className="language-switcher__label" htmlFor="app-language">
                    {t("language.label")}
                </label>
            )}
            <select
                id="app-language"
                className="language-switcher__select"
                value={language}
                onChange={(event) => handleChange(event.target.value)}
                aria-label={t("language.label")}
            >
                {languages.map((item) => (
                    <option key={item.code} value={item.code}>
                        {item.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

export default LanguageSwitcher;
